import { InsertGroupLevelAttributeValueDTO } from '@/jbi-shared/dto/jaas-group-level-attributes.dtos';
import {
  BaseCourse,
  PublicCourseDetails
} from '@/jbi-shared/dto/jtms-public-course.dtos';
import {
  CourseDetails,
  UpdateCourseMasterDTO,
  UpdateCourseTypeDTO
} from '@/jbi-shared/dto/jtms.dtos';
import { DateOrString } from '@/jbi-shared/types/full-document-revision-object.types';
import {
  GroupLevelAttribute,
  GroupLevelAttributeContactsType,
  GroupLevelAttributeOption
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import {
  CourseLocation,
  CourseSortColumns,
  CourseStatus,
  CourseVisibility
} from '@/jbi-shared/types/jtms.types';
import { SortOrder } from '@/jbi-shared/types/search.types';
import { ApiState } from '@/store/types/general.types';
import { Pagination } from '../../admin/types/admin.types';
import { CreateNewGroupRequestPayload } from '../../admin/types/group.types';
import { CreateCourseModuleDTO } from '../course-module/course-module.types';

export interface CourseStatusCountsDTO {
  published: number;
  draft: number;
  inProgress: number;
  cancelled: number;
  completed: number;
}

export interface CourseTypeDTO {
  id: number;
  name: string;
}

export interface CourseTypeOption {
  id?: number;
  name: string;
}

export interface CourseStatusOption {
  id?: number;
  status?: CourseStatus;
  visibility?: CourseVisibility;
  openForRegistration?: 'true' | 'false';
  displayName?: string;
}

export interface CourseLocationOption {
  id: number;
  name: CourseLocation | string;
}

export interface Timezone {
  value: string; // The actual timezone value (e.g., Asia/Kuala_Lumpur)
  label: string; // Formatted label
}

export interface FormattedTimezone {
  display: string;
  original: string;
}

export interface CourseApiState {
  getCourseCountForEachStatus: ApiState;
  getCourseTypes: ApiState;
  getCourses: ApiState;
  getCourseBySlug: ApiState;
  getCourseDetails: ApiState;
  getDefaultCourseLevelAttributes: ApiState;
  getRequiredPublicCourseLevelAttributes: ApiState;
  createNewCourse: ApiState;
  createDraftCourse: ApiState;
  updateCourse: ApiState;
  updateCourseType: ApiState;
  getFullCourseDetailsBySlug: ApiState;
}

export interface CourseState {
  apiState: CourseApiState;
  courseBySlug?: BaseCourse;
  courseCountForEachStatus?: CourseStatusCountsDTO;
  courseTypes?: CourseTypeDTO[];
  courses?: GetCoursesResponsePayload;
  courseDetails?: CourseDetails;
  defaultCourseLevelAttributes: GroupLevelAttribute[] | undefined;
  requiredPublicCourseLevelAttributes: GroupLevelAttribute[];
  createdDraftCourseId?: number;
  createdCourseId?: number;
  courseLevelAttributeState: CourseLevelAttributeState;
  courseCreationState: CourseCreationState;
  courseTabState: CourseTabsState;
  courseLicenseState: CourseLicenseState;
  publicCourseState: PublicCourseState;
}

export interface CourseTabsState {
  activeTabName?: CourseTabsNames;
}

export interface CourseLicenseState {
  shouldCreateWithLicense: boolean;
}

export interface CourseLevelAttributeState {
  locationGLAConfig: GroupLevelAttributeOption[];
  listOfContacts: GroupLevelAttributeContactsType[];
}

export interface CourseCreationState {
  hasModules: boolean;
  hasPackages: boolean;
  isFreeCourse?: boolean;
  hasQuestionnaire?: boolean;
  selectedCourseType?: CourseTypeOption;
  courseSize?: number;
  courseStartDate?: string;
  courseEndDate?: string;
}

export interface PublicCourseState {
  courseDetails?: PublicCourseDetails;
  freeCourseSeatsSelection?: number;
}

export type GetCoursesResponsePayload = Pagination<CourseListItem[]>;

export interface CourseFilterOptions {
  courseName?: string;
  startDate?: DateOrString;
  endDate?: DateOrString;
  organisation?: string;
  type?: string;
  location?: string;
  license?: string;
  status?: string;
  visibility?: string;
  openForRegistration?: string;
}

export interface FilteredCoursePayload extends CourseFilterOptions {
  sortOrder?: SortOrder;
  sortColumn?: CourseSortColumns;
  page: number;
  limit?: number;
}

export interface CourseListItem {
  courseId: number;
  courseName: string;
  courseType: string;
  startDate: DateOrString;
  endDate: DateOrString;
  primaryOrganisation: string;
  relatedJAASGroupId: number;
  location: CourseLocation;
  minParticipants?: number;
  maxParticipants: number;
  participants?: number;
  status: CourseStatus;
  visibility: CourseVisibility;
  openForRegistration: boolean;
  requireRegistrationConfirmation: boolean;
  groupLicenseId?: number;
  applicationId?: string;
}

export interface CreateCourseMasterDTO {
  createGroupPayload: CreateNewGroupRequestPayload;
  createCoursePayload: CreateCourseDTO;
  createGLAPayload: InsertGroupLevelAttributeValueDTO;
  createCourseTrainerPayload?: CreateCourseTrainerDTO;
  // Optional as courses can be created without course modules
  createCourseModulePayload?: CreateCourseModuleDTO;
}

export interface CreateCourseDTO {
  primaryOrganisationId: number;
  courseTypeId: number;
  saveAsDraft: boolean;
  status?: UpdateCourseStatusDTO;
}

export interface UpdateCourseStatusDTO {
  status: CourseStatus;
  visibility: CourseVisibility;
  openForRegistration: boolean;
  requireRegistrationConfirmation: boolean;
}

export interface CreateCourseTrainerDTO {
  selectedTrainerIds: number[];
}

export interface UpdateCourseApiDTO extends UpdateCourseMasterDTO {
  courseId: number;
}

/**
 * Course creation and editing share the same reusable component.
 * However, due to the various scenarios and conditional rendering,
 * true/false boolean as emission events no longer suffice.
 *
 * An enum provides better readability and granularity in specifying
 * event types and helps coordinate actions in other parts of the code.
 *
 * @SAVE_AS_DRAFT Semantically this is 'Save this course as draft',
 * but functionally it is creates course but sets publication status as 'DRAFT'.
 * @CREATE_AND_PUBLISH Creates course and sets publication status to 'PUBLISHED'.
 * @SAVE_SETTINGS Save settings as draft or published courses, with NO update on
 * registration configs.
 * @PUBLISH_SAVE_WITH_REGISTRATION_CHANGES Save settings for published course
 * with registration config changes.
 *
 * Update enum as needed.
 */
export enum CourseCreateAndUpdateButtonEventTypes {
  SAVE_AS_DRAFT = 'create-draft',
  CREATE_AND_PUBLISH = 'create-publish',
  SAVE_SETTINGS = 'save-settings',
  PUBLISH_SAVE_WITH_REGISTRATION_CHANGES = 'save-publish-with-registration-changes'
}

export const CONTACT_OPTIONS_MAX_DISPLAY_COUNT: number = 2;
export const ATTRIBUTE_MAX_DISPLAY_COUNT: number = 4;

export interface CourseRegistrationConfig {
  visibility: CourseVisibility;
  openForRegistration: boolean;
  requireRegistrationConfirmation: boolean;
}

// Default filter state for courses
export const initialFilterState = {
  courseName: '',
  startDate: undefined,
  endDate: undefined,
  organisation: '',
  type: '',
  location: '',
  license: '',
  status: '',
  visibility: '',
  openForRegistration: ''
};

// TODO: Move this to shared module
export interface UpdateCourseTypeApiDTO extends UpdateCourseTypeDTO {
  courseId: number;
}

export interface TabComponent {
  name: CourseTabsNames;
  visible: boolean;
}

export enum CourseTabsNames {
  ATTRIBUTES = 'COURSE_ATTRIBUTES',
  MODULES = 'MODULES',
  PRICING = 'PRICING',
  DISCOUNTS = 'DISCOUNTS',
  COMMUNICATION = 'COMMUNICATION',
  TASKS = 'TASKS',
  ATTENDANCE = 'ATTENDANCE',
  ATTACHMENTS = 'ATTACHMENTS',
  QUESTIONS = 'REGISTRATION_QUESTIONS'
}

/**
 * @example
 * id: string | number; // Unique key for the step, also used to define the slot names
 * label: string;       // Text to display (e.g., "Participants")
 * icon?: string;       // Optional icon override
 * clickable?: boolean; // Can the user click the header to jump to this step?
 * disabled?: boolean;  // Is this step completely disabled?
 */
export interface StepConfig {
  id: string | number;
  label: string;
  icon?: string;
  clickable?: boolean;
  disabled?: boolean;
}
